import api from '@/lib/api';
import type {
  PaginatedResponse,
  ProductNutrientExtra,
  RetailProduct,
} from '@pms/shared';

// ─── Query / payload types ────────────────────────────────────────────────────

/** Per-product nutrition detail submitted on create/update (WAVE 2, F7). */
export interface ProductNutrientsPayload {
  calories?: number | undefined;
  servingSize?: string | undefined;
  extra?: ProductNutrientExtra[] | undefined;
}

export interface ListRetailProductsQuery {
  propertyId: string;
  search?: string | undefined;
  departmentId?: string | undefined;
  isActive?: boolean | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface CreateRetailProductPayload {
  propertyId: string;
  name: string;
  sku: string;
  upc?: string | undefined;
  price: number;
  departmentId: string;
  categoryId?: string | undefined;
  brandId?: string | undefined;
  tags?: string[] | undefined;
  /** WAVE 2 (F7) — menu section for terminal grid grouping. */
  menuSectionId?: string | null | undefined;
  /** WAVE 2 (F7) — allergen lookup ids (replaces the join set). */
  allergenIds?: string[] | undefined;
  /** WAVE 2 (F7) — per-product nutrition detail (upserts the row). */
  nutrients?: ProductNutrientsPayload | undefined;
  taxable?: boolean | undefined;
  isActive?: boolean | undefined;
}

export interface UpdateRetailProductPayload {
  name?: string | undefined;
  sku?: string | undefined;
  upc?: string | undefined;
  price?: number | undefined;
  departmentId?: string | undefined;
  categoryId?: string | undefined;
  brandId?: string | undefined;
  tags?: string[] | undefined;
  /** WAVE 2 (F7) — menu section for terminal grid grouping. */
  menuSectionId?: string | null | undefined;
  /** WAVE 2 (F7) — allergen lookup ids (replaces the join set). */
  allergenIds?: string[] | undefined;
  /** WAVE 2 (F7) — per-product nutrition detail (upserts the row). */
  nutrients?: ProductNutrientsPayload | undefined;
  taxable?: boolean | undefined;
  isActive?: boolean | undefined;
}

export interface GenerateSkuResponse {
  sku: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchRetailProducts(
  query: ListRetailProductsQuery,
): Promise<PaginatedResponse<RetailProduct>> {
  const { data } = await api.get<PaginatedResponse<RetailProduct>>('/retail/products', {
    params: query,
  });
  return data;
}

export async function createRetailProduct(
  payload: CreateRetailProductPayload,
): Promise<RetailProduct> {
  const { data } = await api.post<RetailProduct>('/retail/products', payload);
  return data;
}

export async function updateRetailProduct(
  id: string,
  payload: UpdateRetailProductPayload,
): Promise<RetailProduct> {
  const { data } = await api.patch<RetailProduct>(`/retail/products/${id}`, payload);
  return data;
}

export async function deleteRetailProduct(id: string): Promise<void> {
  await api.delete(`/retail/products/${id}`);
}

export async function generateRetailSku(propertyId: string): Promise<GenerateSkuResponse> {
  const { data } = await api.post<GenerateSkuResponse>(
    '/retail/products/generate-sku',
    undefined,
    { params: { propertyId } },
  );
  return data;
}
